/**
 * I18n — localization with interpolation and pluralization
 */

import ru from './locales/ru.js';
import en from './locales/en.js';
import tr from './locales/tr.js';

const DICTIONARIES = { ru, en, tr };

/**
 * Pluralization rules per locale
 * Returns: 'one', 'few', 'many', 'other'
 */
const PLURAL_RULES = {
  ru(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'one';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
    if (mod10 === 0 || (mod10 >= 5 && mod10 <= 9) || (mod100 >= 11 && mod100 <= 14)) return 'many';
    return 'other';
  },
  en(n) {
    return n === 1 ? 'one' : 'other';
  },
  tr() {
    // Turkish: no grammatical plural distinction in this context
    return 'other';
  },
};

export default class I18n {
  /**
   * @param {string} locale - Language code ('ru', 'en', 'tr')
   * @param {Object} [overrides] - Partial dictionary to override default strings
   */
  constructor(locale, overrides) {
    this._locale = DICTIONARIES[locale] ? locale : 'ru';
    this._dict = this._deepMerge(DICTIONARIES[this._locale], overrides || {});
    this._pluralRule = PLURAL_RULES[this._locale] || PLURAL_RULES.en;
  }

  /**
   * Get localized string by dot-notation key
   * Supports interpolation: t('results.found', { count: 42 })
   * Supports pluralization: {count, plural, one{...} few{...} many{...} other{...}}
   *
   * @param {string} key - Dot-notation key (e.g. 'search.placeholder')
   * @param {Object} [params] - Interpolation values
   * @returns {string}
   */
  t(key, params) {
    let value = this._resolve(key);
    if (value === undefined) {
      console.warn(`[GarderoboSearch] Missing i18n key: "${key}"`);
      return key;
    }

    if (params) {
      // Handle plural expressions: {count, plural, one{...} few{...} many{...} other{...}}
      value = value.replace(
        /\{(\w+),\s*plural,\s*((?:\w+\{[^}]*\}\s*)+)\}/g,
        (_, paramName, forms) => {
          const n = Number(params[paramName]) || 0;
          const category = this._pluralRule(n);
          const match = forms.match(new RegExp(category + '\\{([^}]*)\\}'));
          if (match) return match[1];
          // Fallback to 'other'
          const fallback = forms.match(/other\{([^}]*)\}/);
          return fallback ? fallback[1] : '';
        }
      );

      // Handle simple interpolation: {name}
      value = value.replace(/\{(\w+)\}/g, (_, paramName) =>
        params[paramName] !== undefined ? String(params[paramName]) : `{${paramName}}`
      );
    }

    return value;
  }

  /**
   * Get current locale code
   * @returns {string}
   */
  getLocale() {
    return this._locale;
  }

  /** @private Resolve dot-notation key from dictionary */
  _resolve(key) {
    return key.split('.').reduce((obj, part) => (obj && obj[part] !== undefined ? obj[part] : undefined), this._dict);
  }

  /** @private Deep merge source into target (source wins) */
  _deepMerge(target, source) {
    const result = { ...target };
    for (const key of Object.keys(source)) {
      if (
        source[key] &&
        typeof source[key] === 'object' &&
        !Array.isArray(source[key]) &&
        target[key] &&
        typeof target[key] === 'object'
      ) {
        result[key] = this._deepMerge(target[key], source[key]);
      } else if (source[key] !== undefined) {
        result[key] = source[key];
      }
    }
    return result;
  }
}
